// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeadingComponent, { HeadingProps } from "@store/Library/Components/Toolkit/Heading";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { gridGapStyles } from "@toolkit/Themes/common/mixins";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    reverseColumn = "reverseColumn",
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    title = "title",
    variant = "variant",
    forwardAs = "forwardAs",
    text = "text",
    buttonVariant = "buttonVariant",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.reverseColumn]: boolean;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.title]: HeadingProps["title"];
        [fields.variant]: HeadingProps["variant"];
        [fields.forwardAs]: HeadingProps["forwardAs"];
        [fields.text]: string;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.customCSS]: string;
    };
}

export interface CalloutStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    contentItem?: GridItemProps;
    text?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
}

export const calloutStyles: CalloutStyles = {
    wrapper: {
        css: css`
            ${spacingStyles()}
            padding: ${pxToRem(16)};
            background-color: ${getColor("common.accent")};
        `,
    },
    container: {
        css: css`
            ${gridGapStyles(["1.5rem"])}
        `,
    },
    imageWrapper: {
        css: css`
            ${breakpoint("md", "down")} {
                width: 100%;
                margin-bottom: ${pxToRem(16)};
            }
        `,
    },
    img: {
        css: css`
            background-color: transparent;
            display: block;
            width: 100%;
            height: auto;
        `,
    },
    imageItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
            align-items: center;
        `,
    },
    contentItem: {
        width: [12, 12, 7, 7, 7],
        css: css`
            flex-direction: column;
            justify-content: center;
        `,
    },
    text: {
        css: css`
            ${spacingStyles()}
        `,
    },
};

export const Callout: FC<OwnProps> = ({
    fields: {
        reverseColumn,
        imageUrl,
        altText,
        imageLink,
        title,
        variant,
        forwardAs,
        text,
        buttonVariant,
        buttonLabel,
        buttonLink,
        customCSS,
    },
}) => {
    const { url } = useGetLink(imageLink);
    const history = useHistory();
    const link = useGetLink(buttonLink);
    const { isInShell } = useShellContext();
    const styles = useMergeStyles("image", calloutStyles);

    const onClick = () => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
    };

    const image = <Img className="callout-img" src={imageUrl} altText={altText} loading="lazy" {...styles.img} />;

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css}
            ${customCSS}
        `,
    };

    const customContainerCss = mergeProps<CalloutStyles["container"]>(styles.container, {
        offsetCss: reverseColumn
            ? css`
                  flex-direction: row-reverse;
              `
            : css``,
    });

    const customImageItemCss = mergeProps<CalloutStyles["imageItem"]>(styles.imageItem, {
        css: reverseColumn
            ? css`
                  justify-content: flex-end;
              `
            : css``,
    });

    return (
        <StyledWrapper {...customCssWrapper}>
            <GridContainer {...customContainerCss}>
                <GridItem {...customImageItemCss}>
                    <StyledWrapper className="callout-img-wrapper" {...styles.imageWrapper}>
                        {url ? (
                            <Link className="callout-img-link" href={url} {...styles.imageLink}>
                                {image}
                            </Link>
                        ) : (
                            image
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.contentItem}>
                    {title && (
                        <HeadingComponent title={title} variant={variant} forwardAs={forwardAs}></HeadingComponent>
                    )}
                    {text && <Typography {...styles.text}>{text}</Typography>}
                    {buttonLabel && (
                        <Button variant={buttonVariant} onClick={onClick} className="callout-button" {...styles.button}>
                            {buttonLabel}
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.callout-img-wrapper {
}

.callout-img-link {
}

.callout-img {
}

.callout-button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Callout,
    definition: {
        group: "Common",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.reverseColumn,
                displayName: "Show Image Last",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.imageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.title,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.variant,
                displayName: "Format",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "" },
                    { displayName: "Paragraph", value: "p" },
                    { displayName: "Heading 2", value: "h2" },
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
                defaultValue: "h2",
                hideEmptyOption: true,
            },
            {
                name: fields.forwardAs,
                displayName: "HTML Tag",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Match Format", value: "" },
                    { displayName: "span", value: "span" },
                    { displayName: "p", value: "p" },
                    { displayName: "h2", value: "h2" },
                    { displayName: "h3", value: "h3" },
                    { displayName: "h4", value: "h4" },
                    { displayName: "h5", value: "h5" },
                    { displayName: "h6", value: "h6" },
                ],
                defaultValue: "h2",
                hideEmptyOption: true,
                tooltip: "Set a HTML tag that differs from the visual format for SEO purposes",
            },
            {
                name: fields.text,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.buttonLabel,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
